/* eslint-disable camelcase */
import AutomationCriterionType from '@/entities/AutomationCriterionType.js';
import { useUserStore } from '@/store/modules/user/store.js';
import { get, isEmpty } from 'lodash-es';
import SupplierId from '@/entities/enums/SupplierId.js';
import SupplierType from '@/entities/SupplierType.js';
import Provider from '@/entities/Provider.js';
import { formatPhone } from '@/utils/index.js';
import Model from '@/entities/Model.js';
import User from '@/entities/User.js';
import CommunicationType from '@/entities/CommunicationType.js';
import LeadVehicleType from '@/entities/LeadVehicleType.js';
import AccountGuestGroup from '@/entities/AccountGuestGroup.js';
import ClientCardSection from '@/entities/ClientCardSection.js';
import AccountType from '@/entities/AccountType.js';
import LeadVehicle from '@/entities/LeadVehicle.ts';

export default class Account extends Model {
    static get defaults() {
        return {
            campaigns: [],
            children: [],
            allow_inter_account_leads: false,
            client_card_fields: {
                exchange_vehicle: {},
                general_info: {},
                performance: {},
                process: {},
                wanted_vehicle: {},
            },
            custom_fields: [],
            default_deliveryman_user_id: null,
            default_monthly_mileage: null,
            phone_provider: 'twilio',
            flows: [],
            lead_forms: [],
            parents: [],
            phone_providers: [],
            products: [],
            providers: [],
            sources: [],
            sourcesWithProvider: [],
            response_templates: [],
            suppliers: [],
            teams: [],
            guest_groups: [],
            renewal_alert_options: {
                equity_base: 'rough',
                end_location_notifications: [],
                end_warranty_notifications: [],
                settings: [],
            },
            restrict_to_activix: false,
            imap_ignored_domains: [],
            delivery_guest_group_id: null,
            restrict_lead_status_update_for_roles: [],
            next_step_stopping: 'sale_date',
            homepage: false,
            ai_package: false,
            ai_draft: false,
            niotext: false,
            niotext_campaign: false,
            niotext_phone: false,
            sms_module: false,
            sms_provider_available: null,
        };
    }

    /**
     * Constants
     */
    static get ACTIVIX() {
        return 1;
    }

    static get DEMO() {
        return ['local', 'uat'].includes(process.env.VUE_APP_ENV) ? 5 : 44;
    }

    static get V12() {
        return 824;
    }

    static get ACTIVIX_INTERNAL() {
        return 2510;
    }

    static get ACTIVIX_RECRUTEMENT() {
        return 1190;
    }

    static get ACTIVIX_TEMPLATE() {
        return 1088;
    }

    static get AUTO_ONE_GROUP() {
        return 7790;
    }

    constructor(initialValues = {}) {
        super(initialValues);
    }

    /**
     * Getters
     */

    get children() {
        return this._children;
    }

    get parents() {
        return this._parents;
    }

    get guest_groups() {
        return this._guest_groups;
    }

    get formattedPhone() {
        return formatPhone(this.phone);
    }

    get activeTeams() {
        return this.teams.filter((team) => team.active).sort((a, b) => a.number - b.number);
    }

    get activeUsers() {
        return useUserStore().getActiveUsers(this.id);
    }

    get outgoingPhoneProviders() {
        return this.phone_providers.filter((p) => {
            return p.communication_type_id == CommunicationType.OUTGOING;
        });
    }

    get sources() {
        return this._sources;
    }

    get sourcesWithProvider() {
        return this._sourcesWithProvider;
    }

    get providers() {
        return this._providers;
    }

    get exportableVehicleSuppliers() {
        return this.suppliers.filter((supplier) => {
            return (
                LeadVehicle.exportableSuppliers.includes(supplier.id) &&
                supplier.outbound &&
                supplier.active &&
                !supplier.suspended &&
                !supplier.suspended_temporarily
            );
        });
    }

    get automationCriteriaCustomFields() {
        const customFieldsObject = {
            customFields: {},
            wantedVehiclesCustomFields: {},
            exchangeVehiclesCustomFields: {},
        };

        const criterionType = (type) => {
            switch (type) {
                case 'array':
                    return AutomationCriterionType.SELECT;

                case 'datetime':
                    return AutomationCriterionType.DATETIME;

                case 'boolean':
                    return AutomationCriterionType.BOOLEAN;

                case 'currency':
                    return AutomationCriterionType.CURRENCY;

                default:
                    return AutomationCriterionType.STRING;
            }
        };

        this.getLeadCustomFields().forEach((customField) => {
            customFieldsObject.customFields[customField.id] = criterionType(customField.type);
        });

        this.getLeadVehicleCustomFields().forEach((customField) => {
            if (ClientCardSection.isVehicleWantedField(customField.section)) {
                customFieldsObject.wantedVehiclesCustomFields[customField.id] = criterionType(
                    customField.type
                );
            } else if (ClientCardSection.isVehicleExchangeField(customField.section)) {
                customFieldsObject.exchangeVehiclesCustomFields[customField.id] = criterionType(
                    customField.type
                );
            }
        });

        return customFieldsObject;
    }

    get hasTRS() {
        return this.providers.some((provider) => provider.id === Provider.TRS);
    }

    get responseTemplates() {
        return this._response_templates;
    }

    /**
     * Setters
     */

    set children(children) {
        this._children = children.map((child) => new Account(child));
    }

    set parents(parents) {
        this._parents = parents.map((parent) => new Account(parent));
    }

    set users(users) {
        console.error('Need to be removed. Users is loaded in UserStore');
    }

    get users() {
        return useUserStore().getUsers(this.id);
    }

    set guest_groups(groups) {
        this._guest_groups = groups.map((group) => new AccountGuestGroup(group));
    }

    set sources(sourcesArg) {
        const initSources = (sources) => {
            const _sources = [];
            const addSourceToList = (newSource, currentAccount) => {
                const sourceAlreadyInList = _sources.some(
                    (source) => source.name.trim() === newSource.name.trim()
                );

                if (sourceAlreadyInList) {
                    return;
                }

                if (!newSource.display_on_listing && !currentAccount.display_approved_sources) {
                    return;
                }
                _sources.push(newSource);
            };

            sources.forEach((newSource) => addSourceToList(newSource, this));

            if (this.children) {
                this.children.forEach((childAccount) => {
                    childAccount._sources.forEach((newSource) =>
                        addSourceToList(newSource, childAccount)
                    );
                });
            }

            return _sources;
        };

        this._sources = initSources(sourcesArg);

        const initProviders = () => {
            const providers = [];
            const addProviderToList = (source) => {
                if (!source.provider) {
                    return;
                }

                const providerAlreadyInList = providers.some((provider) => {
                    return provider.name.trim() === source.provider.name?.trim();
                });

                if (providerAlreadyInList) {
                    return;
                }

                providers.push({
                    id: source.provider.id,
                    name: source.provider.name,
                    display_name: source.provider.display_name,
                });
            };

            sourcesArg
                .filter((source) => source.active && source.display_on_listing)
                .forEach(addProviderToList);

            if (this.children) {
                this.children.forEach((childAccount) => {
                    childAccount._sourcesWithProvider
                        .filter((source) => source.active && source.display_on_listing)
                        .forEach(addProviderToList);
                });
            }
            return providers;
        };

        this._providers = initProviders();

        const initSourcesWithProviders = () => {
            const sources = [];
            const addSourceToList = (newSource) => {
                const sourceInList =
                    sourcesArg.filter((source) => source.name.trim() === newSource.name.trim())
                        .length > 1;

                sources.push({
                    ...newSource,
                    name: sourceInList
                        ? `${newSource.name} (${newSource.provider?.name})`
                        : newSource.name,
                });
            };

            sourcesArg.forEach(addSourceToList);

            if (this.children) {
                this.children.forEach((childAccount) => {
                    sources.concat(childAccount._sourcesWithProvider);
                });
            }
            return sources;
        };

        this._sourcesWithProvider = initSourcesWithProviders();
    }

    set response_templates(responseTemplates) {
        this._response_templates = responseTemplates.concat(
            this.parents.map((parentAccount) => parentAccount.responseTemplates).flat()
        );
    }

    /**
     * Specifications
     */

    hasOption(option) {
        return !!this[option];
    }

    hasVehiclesModelTextOption() {
        return this.vehicle_text || this.vehicle_model_text;
    }

    hasNioText() {
        return this.niotext && this.niotext_phone;
    }

    hasSmsV2Native() {
        return this.niotext && this.sms_module;
    }

    hasSmsCampaignModule() {
        return this.hasSmsV2Native() && !!this.niotext_campaign;
    }

    hasSmsCapability() {
        return this.hasNioText() || this.hasSmsV2Native();
    }

    hasAiDraft() {
        return !!this.ai_package && !!this.ai_draft;
    }

    enabledWaitingSale() {
        return !!this.waiting_sale_option && !!this.waiting_sale_date;
    }

    hasMessenger() {
        return !!this.facebook_id;
    }

    hasSupplier(supplierId) {
        return this.suppliers.some((supplier) => {
            return supplier.id == supplierId && supplier.inbound && supplier.outbound;
        });
    }

    hasInventorySupplier() {
        return this.suppliers.some((supplier) => supplier.type === SupplierType.INVENTORY);
    }

    hasActiveInventorySupplier() {
        return this.suppliers.some(
            (supplier) => supplier.type === SupplierType.INVENTORY && supplier.active
        );
    }

    hasSuppliersUserSetting() {
        return this.suppliers.some((supplier) => supplier.user_setting);
    }

    hasHyundaiSupplier() {
        return this.suppliers.some((supplier) => supplier.id === SupplierId.HYUNDAI);
    }

    isParent() {
        return !!this.account_manager;
    }

    isChild() {
        return !!this.parents.length;
    }

    isCustom() {
        return this.type == AccountType.CUSTOM;
    }

    isDealer() {
        return this.type == AccountType.DEALER;
    }

    isPolestar() {
        return this.type == AccountType.POLESTAR;
    }

    getUserFromCache(userId, withParentUser = false) {
        return (this._users || []).find(
            (user) => user.id == userId || (withParentUser && user.parent_user_id == userId)
        );
    }

    set providers(providers) {
        // not used
        // get providers code was creating the data based on this._sources
    }

    set sourcesWithProvider(sources) {
        // not used
        // get sourcesWithProvider code was creating the data based on this._sourcesWithProvider
    }

    getUser(userId) {
        return useUserStore().getUserById(userId);
    }

    async getUserOrChild(userId) {
        return useUserStore().getUserById(userId);
    }

    getAllCustomFields() {
        let customFields = this.custom_fields;

        if (!this.parents || isEmpty(this.parents)) {
            return customFields;
        }

        this.parents.forEach((parentAccount) => {
            customFields = customFields.concat(
                parentAccount.custom_fields.map((customField) => {
                    customField.parentName = parentAccount.name;

                    return customField;
                })
            );
        });

        return customFields;
    }

    getLeadCustomFields() {
        return this.getAllCustomFields().filter((customField) =>
            ClientCardSection.isLeadField(customField.section)
        );
    }

    getLeadVehicleCustomFields() {
        return this.getAllCustomFields().filter((customField) =>
            ClientCardSection.isVehicleField(customField.section)
        );
    }

    getSectionCustomFields(section) {
        return this.getAllCustomFields().filter((customField) => customField.section === section);
    }

    getSectionActiveCustomFields(section) {
        return this.getAllCustomFields().filter(
            (customField) => customField.visible && customField.section === section
        );
    }

    getCustomFieldName(field) {
        const fieldObject = this.getAllCustomFields().find((customField) => {
            return customField.field == field;
        });
        return fieldObject ? fieldObject.name : field.replace('custom_', '');
    }

    getCustomFieldById(fieldId) {
        return this.getAllCustomFields().find((customField) => {
            return customField.id == fieldId;
        });
    }

    getCustomFieldByField(field) {
        return this.getAllCustomFields().find((customField) => {
            return customField.field == field;
        });
    }

    hasSectionCustomFields(section) {
        return this.getSectionCustomFields(section).some((customField) => customField.visible);
    }

    hasGeneralInfoField(field, fallback = true) {
        field = Account.normalizeClientCardFieldNameForValidation(field);
        return get(this.client_card_fields, `general_info.${field}`, fallback);
    }

    hasPerformanceField(field, fallback = true) {
        field = Account.normalizeClientCardFieldNameForValidation(field);
        return get(this.client_card_fields, `performance.${field}`, fallback);
    }

    hasProcessField(field, fallback = true) {
        field = Account.normalizeClientCardFieldNameForValidation(field);
        return get(this.client_card_fields, `process.${field}`, fallback);
    }

    hasVehicleField(field, type, fallback = true) {
        switch (type) {
            case 'exchange':
            case 'exchange_vehicle':
                return this.hasExchangeVehicleField(field, fallback);
            case 'wanted':
            case 'wanted_vehicle':
                return this.hasWantedVehicleField(field, fallback);
            default:
                return fallback;
        }
    }

    hasExchangeVehicleField(field, fallback = true) {
        field = Account.normalizeClientCardFieldNameForValidation(field);
        return get(this.client_card_fields, `exchange_vehicle.${field}`, fallback);
    }

    hasWantedVehicleField(field, fallback = true) {
        field = Account.normalizeClientCardFieldNameForValidation(field);
        return get(this.client_card_fields, `wanted_vehicle.${field}`, fallback);
    }

    haveCustomPhoneProvider() {
        return this.phone_provider != 'twilio';
    }

    hasClientCardField(field, vehicleType = '') {
        field = Account.normalizeClientCardFieldNameForValidation(field);

        const generalInfo = [
            'budget',
            'communication_preference',
            'sector',
            'rating',
            'lead_type',
            'lead_form',
            'created_method',
            'source',
            'division',
            'comment',
            'campaign',
            'segment',
            'storage',
            'client_number',
            'called_count',
            'call_date',
            'prepaid',
            'business',
            'loyalty',
            'referrer',
            'search_term',
            'keyword',
            'navigation_history',
            'do_not_market',
            'non_consumer_initiated',
        ];
        const performance = [
            'dealer_tour',
            'deposit',
            'qualification',
            'refinanced',
            'walk_around',
            'phone_appointment',
            'appointment',
            'presented',
            'sale_by_phone',
            'sale',
            'renew',
            'road_test',
            'long_term',
            'take_over',
            'be_back',
            'write_up',
        ];
        const process = [
            'available',
            'deliverable',
            'discounted',
            'gas',
            'recorded',
            'lead_bank',
            'progress_state',
            'approved',
            'delivery',
            'waiting_sale',
            'prepared',
            'inspected',
            'delivered',
            'csi',
            'prepared_work_order',
        ];
        const vehicle = [
            'allowed_mileage',
            'bodyType',
            'budget',
            'category',
            'category_rv',
            'color_exterior',
            'color_interior',
            'comment',
            'drivingWheels',
            'end_contract_date',
            'end_extended_warranty_date',
            'end_extended_warranty_mileage',
            'end_warranty_date',
            'end_warranty_mileage',
            'engine',
            'frequency',
            'fuel',
            'initial_cash',
            'in_service_date',
            'length',
            'mechanical',
            'mileage',
            'modality',
            'offer_number',
            'payment',
            'payment_with_tax',
            'price',
            'profit',
            'rate',
            'residual',
            'security_deposit',
            'sleeping',
            'stock',
            'term',
            'transmission',
            'vin',
            'vin_decoded',
            'warranty',
            'warranty_type',
            'weight',
            'year',
        ];
        const exchangeVehicle = [
            'accidented_damage_cost',
            'balance',
            'condition',
            'extended_warranty',
            'institution',
            'intention',
            'license_plate',
            'link',
            'mileage_excess',
            'purchase_date',
            'recall',
            // 'refinance',
            // 'renewal',
            'requested',
            'sold_by',
            'sold_date',
            'tire_type',
            'value',
            ...vehicle,
        ];
        const wantedVehicle = [
            'accessories',
            'documentation',
            'order_number',
            'preparation',
            'suffix',
            'tire',
            'total',
            ...vehicle,
        ];

        switch (true) {
            case generalInfo.includes(field):
                return this.hasGeneralInfoField(field);
            case performance.includes(field):
                return this.hasPerformanceField(field);
            case process.includes(field):
                return this.hasProcessField(field);
            case vehicleType == LeadVehicleType.EXCHANGE && exchangeVehicle.includes(field):
                return this.hasExchangeVehicleField(field);
            case vehicleType == LeadVehicleType.WANTED && wantedVehicle.includes(field):
                return this.hasWantedVehicleField(field);
            default:
                return true;
        }
    }

    static normalizeClientCardFieldNameForValidation(field) {
        switch (field) {
            case 'appointment_date':
                return 'appointment';
            case 'available_date':
                return 'available';
            case 'bank_id':
                return 'institution';
            case 'be_back_date':
                return 'be_back';
            case 'body_type':
                return 'bodyType';
            case 'budget_max':
            case 'budget_min':
                return 'budget';
            case 'deliverable_date':
                return 'deliverable';
            case 'delivered_date':
                return 'delivered';
            case 'delivery_date':
                return 'delivery';
            case 'discounted_date':
                return 'discounted';
            case 'division_id':
                return 'division';
            case 'driving_wheels':
                return 'drivingWheels';
            case 'extended_warranty':
                return 'warranty';
            case 'form_normalized':
                return 'lead_form';
            case 'lead_bank_id':
                return 'lead_bank';
            case 'lead_type_id':
                return 'lead_type';
            case 'length_max':
            case 'length_min':
                return 'length';
            case 'offer':
                return 'offer_number';
            case 'presented_date':
                return 'presented';
            case 'source_id':
                return 'source';
            case 'recorded_date':
                return 'recorded';
            case 'refinanced_date':
            case 'refinanced_type':
                return 'refinanced';
            case 'road_test_date':
                return 'road_test';
            case 'sale_date':
                return 'sale';
            case 'stock_state':
                return 'stock';
            case 'take_over_date':
                return 'take_over';
            case 'year':
                // Required as the client_card_field for min/max year has the same name
                return 'year_current';
            case 'year_max':
            case 'year_min':
                return 'year';
            default:
                return field;
        }
    }

    get userIds() {
        return this._users.map((user) => user.id);
    }

    getActiveUsers() {
        return useUserStore().getActiveUsers(this.id);
    }

    get advisorUsers() {
        return this.activeUsers.filter((user) => user.isAdvisor() || user.isDirector());
    }

    get bdcUsers() {
        return this.activeUsers.filter(
            (user) => user.isBdc() || user.isBdcDirector() || user.isDirector()
        );
    }

    get directorUsers() {
        return this.activeUsers.filter((user) => user.isDirector());
    }

    get commercialUsers() {
        return this.activeUsers.filter((user) => user.isCommercialDirector() || user.isDirector());
    }

    get serviceAdvisorUsers() {
        return this.activeUsers.filter(
            (user) => user.isServiceAdvisor() || user.isServiceDirector()
        );
    }

    get serviceAgentUsers() {
        return this.activeUsers.filter((user) => user.isServiceAgent() || user.isServiceDirector());
    }

    getUsersAndSupplierUsers() {
        const users = this.users;
        const supplierUsers = [];

        const addSupplierUser = (supplier) => {
            if (supplierUsers.some((user) => user.id == supplier.user_id)) {
                return;
            }

            supplierUsers.push(
                new User({
                    id: supplier.user_id,
                    supplier_id: supplier.id,
                    first_name: supplier.user_fullname || supplier.name,
                })
            );
        };

        this.suppliers.forEach(addSupplierUser);

        if (this.hasNioText()) {
            users.push(
                new User({
                    id: User.NIOTEXT_USER,
                    first_name: 'NioText',
                    active: true,
                })
            );
        }

        if (this.children.length) {
            this.children.forEach((childAccount) => {
                users.push(...childAccount.users);
                childAccount.suppliers.forEach(addSupplierUser);
            });
        }

        if (this.parents.length) {
            this.parents.forEach((parentAccount) => {
                users.push(...parentAccount.users);
                parentAccount.suppliers.forEach(addSupplierUser);
            });
        }

        return [...users, ...supplierUsers].sort((a, b) => a.fullName.localeCompare(b.fullName));
    }

    getActiveUsersAndSupplierUsers() {
        const usersAndSupplierUsers = this.getUsersAndSupplierUsers();

        return usersAndSupplierUsers.filter((user) => {
            if (!user.active && user.supplier_id === null && !user.api_user) {
                return false;
            }

            if (!user.parent_user_id) {
                return true;
            }

            return !usersAndSupplierUsers.some((u) => u.id === user.parent_user_id);
        });
    }
}
